//  Angular imports
import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnInit, inject } from '@angular/core';
//  Application imports
import { ApplicationRoute } from 'app/models/application-route';
import { HKeyManagerService } from 'app/services/hkey-manager.service';
import { RAccount } from 'app/records/account.record';
import { RHKeyData } from 'app/records/h-key-data.record';
import { UserService } from 'app/services/user.service';
//  Third party imports
import { jwtDecode } from 'jwt-decode';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { RAccountInformationDetails } from 'app/records/account-information-details-records';
import { RNode } from 'app/records/organization-record';
import { LoadingIndicatorComponent } from '../loading-indicator/loading-indicator.component';

@Component({
    selector: 'zf-login',
    template: '<div style="width:100%; height: calc(100vh - 102px);"></div><zf-loading-indicator></zf-loading-indicator>',
    imports: [LoadingIndicatorComponent]
})
export class LoginComponent implements OnInit {
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private oidcSecurityService = inject(OidcSecurityService);
    private userService = inject(UserService);
    private hKeyManagerService = inject(HKeyManagerService);

    public ngOnInit(): void {
        // check, and subscribe
        // to changes on the authentication state
        this.oidcSecurityService
            .checkAuth()
            .subscribe((loginRes: LoginResponse) => {

                if (!loginRes.isAuthenticated) {

                    console.error('we are unauthenticated');
                    this.userService.clearAuthenticated();
                    this.router.navigateByUrl(ApplicationRoute.SPLASH);
                    return;
                }
                const data = this.route.snapshot.queryParamMap.get('state');
                // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                const decoded: any = jwtDecode(loginRes.idToken);
                // do we have an IAM username, if not resolve the userId
                let userId = decoded['custom:IAM_username'] ?? '';
                if (userId === '') {
                    userId = decoded['identities'][0]['userId'];
                    // if we don't have an '@' we're a fortinet address
                    // but we don't get that back from the token
                    // so we need to add it in
                    userId = userId.indexOf('@') === -1 ? `${userId}@fortinet.com` : userId;
                }

                const accId = decoded['custom:IAM_account_name'] ?? null;
                this.userService.setAuthenticated();
                this.userService.setGlobalUserId(userId);
                this.userService.setGlobalIAMAccount(accId);
                let hKeyData: RHKeyData = RHKeyData.fromBase64Encoded(data);

                // we have no previous login details
                // i.e we have not come from another portal
                if (hKeyData === null) {
                    // we need to load up the accounts
                    // do we have hkeydata?
                    // if we do we can use it to determine our last account

                    hKeyData = this.hKeyManagerService.get() ?? null;
                    if (hKeyData !== null && !hKeyData.isOrg) {
                        this.getNonOrgUser(hKeyData);
                    } else if (hKeyData != null && hKeyData.isOrg) {
                        this.getOrgUser(hKeyData);
                    }
                    else {
                        this.router.navigateByUrl(ApplicationRoute.ACCOUNT_SELECTION);
                    }
                } else {
                    this.hKeyManagerService.store(hKeyData);
                    if (hKeyData.nodeId !== "") {
                        this.getOrgUser(hKeyData);
                    } else {
                        this.getNonOrgUser(hKeyData);
                    }
                }
            });
    }

    private getNonOrgUser(hKeyData: RHKeyData): void {
        this.userService.getAccount(
            parseInt(hKeyData.accountId, 10),
            parseInt(hKeyData.userId, 10)
        ).subscribe((account: RAccount): void => {
            this.userService.selectAccount(account);
            this.router.navigateByUrl(ApplicationRoute.ACCOUNT_SELECTION);
        });
    }

    private getOrgUser(hKeyData: RHKeyData): void {
        this.userService.getAccounts(false)
            .subscribe((acc: RAccountInformationDetails) => {
                if (acc.hasOrg) {
                    const nodeId = parseInt(hKeyData.nodeId, 10)
                    this.userService.setOrganizations(acc.organizations);
                    this.userService.getOrgPermissions(
                        nodeId
                    ).subscribe((account: RAccount): void => {
                        let selectedNode: RNode = null;
                        for (const o of acc.organizations) {
                            for (const r of o.nodes) {
                                if (r.nodeId === nodeId) {
                                    selectedNode = r;
                                    break;
                                }

                            }
                            if (selectedNode)
                                break;
                        }
                        account.details.selectedNode = selectedNode;
                        this.userService.selectAccount(account);
                        this.router.navigateByUrl(ApplicationRoute.ACCOUNT_SELECTION);
                    });
                }
            });
    }
}
